import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Car, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  FUEL_TYPE_OPTIONS,
  VEHICLE_CATEGORY_OPTIONS,
  ACQUISITION_TYPE_OPTIONS,
} from "../../lib/fleet-constants";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function CreateVehicleModal({ open, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  
  const [form, setForm] = useState({
    brand: "",
    model: "",
    registration: "",
    acquisition_type: "lld",
    trim: "",
    year: "",
    fuel_type: "",
    category: "",
    driver_employee_id: "",
    assignment: "",
  });
  
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (open) {
      fetchEmployees();
    }
  }, [open]);

  const fetchEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await axios.get(`${API}/fleet/employees/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.employees || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!form.brand.trim()) {
      toast.error("Veuillez saisir la marque");
      return false;
    }
    if (!form.model.trim()) {
      toast.error("Veuillez saisir le modèle");
      return false;
    }
    if (!form.registration.trim()) {
      toast.error("Veuillez saisir l'immatriculation");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const payload = {
        brand: form.brand.trim(),
        model: form.model.trim(),
        registration: form.registration.trim().toUpperCase(),
        acquisition_type: form.acquisition_type,
      };
      
      if (form.trim) payload.trim = form.trim.trim();
      if (form.year) payload.year = parseInt(form.year);
      if (form.fuel_type) payload.fuel_type = form.fuel_type;
      if (form.category) payload.category = form.category;
      if (form.driver_employee_id) payload.driver_employee_id = form.driver_employee_id;
      if (form.assignment) payload.assignment = form.assignment.trim();
      
      const response = await axios.post(`${API}/fleet/vehicles`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Véhicule créé", {
        description: `${form.brand} ${form.model} - ${response.data.internal_code}`
      });
      
      // Reset form
      setForm({
        brand: "",
        model: "",
        registration: "",
        acquisition_type: "lld",
        trim: "",
        year: "",
        fuel_type: "",
        category: "",
        driver_employee_id: "",
        assignment: "",
      });
      
      onCreated?.(response.data);
    } catch (err) {
      console.error("Error creating vehicle:", err);
      // Handle Pydantic validation errors (array of objects) or simple string errors
      let errorMessage = "Impossible de créer le véhicule";
      const detail = err.response?.data?.detail;
      if (detail) {
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          // Pydantic validation error format
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast.error("Erreur", {
        description: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Car className="w-5 h-5 text-indigo-600" />
            Ajouter un véhicule
          </DialogTitle>
          <DialogDescription>
            Renseignez les informations principales. Vous pourrez compléter la fiche ensuite.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Required Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="brand">Marque *</Label>
                <Input
                  id="brand"
                  placeholder="Ex: Renault"
                  value={form.brand}
                  onChange={(e) => handleChange("brand", e.target.value)}
                  data-testid="vehicle-brand"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model">Modèle *</Label>
                <Input
                  id="model"
                  placeholder="Ex: Megane"
                  value={form.model}
                  onChange={(e) => handleChange("model", e.target.value)}
                  data-testid="vehicle-model"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="registration">Immatriculation *</Label>
                <Input
                  id="registration"
                  placeholder="Ex: AB-123-CD"
                  value={form.registration}
                  onChange={(e) => handleChange("registration", e.target.value.toUpperCase())}
                  className="font-mono"
                  data-testid="vehicle-registration"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="acquisition_type">Type de contrat *</Label>
                <Select
                  value={form.acquisition_type}
                  onValueChange={(v) => handleChange("acquisition_type", v)}
                >
                  <SelectTrigger id="acquisition_type" data-testid="vehicle-acquisition-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACQUISITION_TYPE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Optional Fields */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm text-slate-500 mb-3">Informations optionnelles</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="trim">Finition</Label>
                  <Input
                    id="trim"
                    placeholder="Ex: Intens"
                    value={form.trim}
                    onChange={(e) => handleChange("trim", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="year">Année</Label>
                  <Input
                    id="year"
                    type="number"
                    placeholder="Ex: 2023"
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    value={form.year}
                    onChange={(e) => handleChange("year", e.target.value)}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Carburant</Label>
                  <Select
                    value={form.fuel_type}
                    onValueChange={(v) => handleChange("fuel_type", v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {FUEL_TYPE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select
                    value={form.category}
                    onValueChange={(v) => handleChange("category", v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {VEHICLE_CATEGORY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Conducteur</Label>
                  <Select
                    value={form.driver_employee_id || "none"}
                    onValueChange={(v) => handleChange("driver_employee_id", v === "none" ? "" : v)}
                    disabled={loadingEmployees}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingEmployees ? "Chargement..." : "Sélectionner..."} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name} ({emp.matricule})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="assignment">Affectation</Label>
                  <Input
                    id="assignment"
                    placeholder="Ex: Direction"
                    value={form.assignment}
                    onChange={(e) => handleChange("assignment", e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-vehicle">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Créer le véhicule
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
